import { sum } from 'es-toolkit';
import { currentEpisode } from '../global-state/audio-signals';
import { setEpisodeProgress, useUserDataStore } from '../global-state/user-data';
import { Episode } from '../models/podcasts';
import debugLog from '../utils/debug';
import { reportSentryWarning } from '../utils/error-handling';

const log = (...args: any[]) => {
  debugLog({ logName: 'audio-state', logColor: 'darkblue' }, ...args);
};

//
// Preload initial episode
//

export function preloadInitialEpisode(
  loadNewEpisode: (episode: Episode, seek: number) => void
) {
  const episode = currentEpisode.value;
  const seekFromLastSession =
    'seekFromLastSession' in window ? window.seekFromLastSession : null;
  log(
    'Initial episode:',
    episode ? episode.id : '(none)',
    'Seek from last session:',
    seekFromLastSession ? JSON.stringify(seekFromLastSession) : '(none)'
  );
  if (episode) {
    const userDataState = useUserDataStore.getState();
    const progress = userDataState.episodesProgress[episode.id];
    const progressTimestamp = userDataState.episodesProgressSaveTimes[episode.id];
    let seek = (progress && progress[0]) || 0;
    if (seekFromLastSession) {
      const { seek: nativeSeek, timestampMs: nativeTimestamp } = seekFromLastSession;
      const matchesCurrentEpisode =
        'episodeId' in seekFromLastSession // Android
          ? episode.id === seekFromLastSession.episodeId
          : 'url' in seekFromLastSession // iOS
            ? episode.audioUrl === seekFromLastSession.url
            : false;
      if (progressTimestamp > nativeTimestamp) {
        log(
          'Native seek is older than user-data seek',
          progressTimestamp,
          nativeTimestamp
        );
      } else if (matchesCurrentEpisode && Math.abs(seek - nativeSeek) > 2) {
        // Natively stored seek for this episode differs significantly from seek in LocalStorage
        // Overwrite locally stored value if newer
        const duration =
          episode.duration || Math.max(nativeSeek, progress ? sum(progress) : 0);
        setEpisodeProgress(episode, nativeSeek, duration, nativeTimestamp);
        const updatedProgress =
          useUserDataStore.getState().episodesProgress[episode.id];
        if (
          updatedProgress[0] !== seek &&
          Math.abs(updatedProgress[0] - nativeSeek) < 2
        ) {
          log(`Seek corrected by native app: ${seek} -> ${nativeSeek}`);
          seek = updatedProgress[0];
        } else {
          // Should never happen, but just in case
          log(`Native app failed to correct seek`);
          log('progress:', progress, progressTimestamp);
          log('updatedProgress:', updatedProgress);
          log('seekFromLastSession:', seekFromLastSession);
          reportSentryWarning(new Error(`Native app failed to correct seek`), {
            progress,
            updatedProgress,
            seekFromLastSession,
          });
        }
      } else if (matchesCurrentEpisode) {
        log('Initial seek aligns with native app', seek, nativeSeek);
      } else {
        log('Initial episode differs from native app');
      }
    }
    loadNewEpisode(episode, seek);
  }
}
